import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ButtonBase } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import DateRangeIcon from "@mui/icons-material/DateRange";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import { COPY_PLAN, EDIT_PLAN, SELECT_PLAN, SVG } from "../../consts/sentryIds";
import {
  DAY,
  EDIT_MODE_ACTIVE_WARNING,
  MAP_EDIT_MODE_ACTIVE_WARNING,
  PLEASE_SELECT,
} from "../../consts/strings";
import { palette } from "../../theme/palette";

import { findProfile, formatProfileName } from "../../utils/plans";

import {
  addNotification,
  setCopyModalOpen,
  setEditPlanModalOpen,
  setSelectPlanModalOpen,
  setPlanManagementReloadModalOpen,
} from "../../reducers/ownApp";
import {
  editPlanModalOpenSelector,
  selectPlanModalOpenSelector,
  showReloadPlanManagementSelector,
} from "../../reducers/ownApp/selectors";
import {
  getMatrixProfiles,
  getSelectedMatrixProfile,
  isEditModeSelector,
  isProfileValidSelector,
} from "../../reducers/matrixProfile/selectors";
import { mapEditModeSelector } from "../../reducers/depot/selectors";

import EditPlanModal from "../Modals/EditPlanModal/EditPlanModal";
import SelectPlanModal from "../Modals/SelectPlanModal/SelectPlanModal";
import { View } from "../View";
import { ReloadIcon } from "../Icons";

const useStyles = makeStyles({
  container: {
    marginLeft: "20px",
    alignItems: "center",
    display: "flex",
  },
  text: {
    color: palette.typography.white.high,
    fontSize: "12px",
    marginRight: "4px",
  },
  icon: {
    width: "12px",
    height: "12px",
    margin: 0,
    color: palette.typography.white.high,
    cursor: "pointer",
  },
  reloadIcon: {
    width: "1rem",
    height: "1rem",
    fill: "white",
  },
  iconButton: {
    width: "12px",
    marginRight: "8px",
  },
  disabled: {
    color: palette.typography.white.disabled,
  },
});

export const PlanSelector = () => {
  const dispatch = useDispatch();

  const selectedProfile = useSelector(getSelectedMatrixProfile);
  const { dayProfiles, datedProfiles, templates } =
    useSelector(getMatrixProfiles);
  const [profile, setProfile] = useState();
  const styles = useStyles();
  const selectPlanModalOpen = useSelector(selectPlanModalOpenSelector);
  const editPlanModalOpen = useSelector(editPlanModalOpenSelector);
  const showReloadPlanManagement = useSelector(
    showReloadPlanManagementSelector
  );
  const isEditMode = useSelector(isEditModeSelector);
  const mapEditMode = useSelector(mapEditModeSelector);
  const isProfileValid = useSelector(isProfileValidSelector);

  useEffect(() => {
    setProfile(
      findProfile({
        selectedProfile,
        dayProfiles,
        datedProfiles,
        templates,
      })
    );
  }, [datedProfiles, dayProfiles, selectedProfile, templates]);

  const editModeOnWarning = () => {
    dispatch(
      addNotification({
        message: EDIT_MODE_ACTIVE_WARNING,
        options: { variant: "info" },
      })
    );
  };

  const mapEditModeWarning = () => {
    dispatch(
      addNotification({
        message: MAP_EDIT_MODE_ACTIVE_WARNING,
        options: { variant: "info" },
      })
    );
  };

  const openModal = (action) => {
    if (isEditMode) editModeOnWarning();
    else if (mapEditMode) mapEditModeWarning();
    else dispatch(action(true));
  };

  return (
    <>
      <View className={styles.container}>
        <Typography className={styles.text}>Plan:</Typography>
        <Typography className={styles.text}>
          {formatProfileName({ type: selectedProfile?.type, profile }) ??
            PLEASE_SELECT}
        </Typography>
        <ButtonBase
          onClick={() => openModal(setSelectPlanModalOpen)}
          className={styles.iconButton}
          sentryId={SELECT_PLAN}
        >
          <DateRangeIcon
            className={styles.icon}
            sentryId={`${SELECT_PLAN}__${SVG}`}
          />
        </ButtonBase>
        {selectedProfile && selectedProfile?.type !== DAY && (
          <ButtonBase
            onClick={() => openModal(setEditPlanModalOpen)}
            className={styles.iconButton}
            sentryId={EDIT_PLAN}
          >
            <CreateIcon
              sentryId={`${EDIT_PLAN}__${SVG}`}
              className={styles.icon}
            />
          </ButtonBase>
        )}
        {selectedProfile && !showReloadPlanManagement && (
          <ButtonBase
            onClick={() => openModal(setCopyModalOpen)}
            className={styles.iconButton}
            disabled={!isProfileValid}
            sentryId={COPY_PLAN}
          >
            <FileCopyIcon
              sentryId={`${COPY_PLAN}__${SVG}`}
              className={clsx(styles.icon, !isProfileValid && styles.disabled)}
            />
          </ButtonBase>
        )}
        {selectedProfile && showReloadPlanManagement && (
          <ButtonBase>
            <ReloadIcon
              className={styles.reloadIcon}
              onClick={() => dispatch(setPlanManagementReloadModalOpen(true))}
            />
          </ButtonBase>
        )}
      </View>
      <SelectPlanModal
        open={selectPlanModalOpen}
        onClose={() => dispatch(setSelectPlanModalOpen(false))}
      />
      <EditPlanModal
        open={editPlanModalOpen}
        onClose={() => dispatch(setEditPlanModalOpen(false))}
        profile={profile}
      />
    </>
  );
};
